import React, { useState, useContext } from "react";

import {
  Card,
  Layout,
  Stack,
  TextContainer,
  FormLayout,
  TextField,
  ResourceItem,
  Thumbnail,
  ResourceList,
  Modal,
  DisplayText,
  TextStyle,
} from "@shopify/polaris";
import { EditMinor, DeleteMinor } from "@shopify/polaris-icons";
import constant from "lib/constant/constant";
import baseHelper from "lib/helpers/base";
import { useMutation } from "react-apollo";
import { PrivateContext } from "lib/context";
import { imageHelper, errorHelper } from "lib/helpers";
import Popover from "lib/components/popover/popover";
import { BULK_UPDATE_VARIANT, DELETE_VARIANT } from "../../../../../../apollo/mutations";
import { variantsProp } from "../../../../../generic/edit/propTypes";

const RenderVariants = (props) => {
  const { history, cms } = useContext(PrivateContext);
  const { data, currentUser = {}, handleBanner, refetch } = props || {};
  const { _id: productItemId } = data;
  const { symbol } = constant;
  const { moneyFormat = symbol.DOLLAR } = currentUser;

  const [selectedItems, setSelectedItems] = useState([]);
  const [active, setActive] = useState(false);
  const [openQuantity, setOpenQuantity] = useState(false);
  const [deleteActive, setDeleteActive] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [updatePrice, setPrice] = useState("");
  const [updateQuantity, setQuantity] = useState("");
  const [activePopover, setActivePopover] = useState({});

  const { gql } = constant;

  // GQL calls
  const [updateMarkupVariant, { loading: loadingBulkUpdate }] = useMutation(BULK_UPDATE_VARIANT);
  const [deleteVariant, { loading: loadingDeleteVariant }] = useMutation(DELETE_VARIANT);

  const handleEditMarkup = () => setActive(!active);
  const handleEditQuantity = () => setOpenQuantity(!openQuantity);

  const handleDeleteModal = (index) => {
    setSelectedId(index);
    setDeleteActive(!deleteActive);
  };
  const bulkActions = [
    { content: cms("section.variant.common.updatePrice"), onAction: () => handleEditMarkup() },
    { content: cms("section.modal.button.primary.quantity"), onAction: () => handleEditQuantity() },
  ];

  const handleChange = (fieldValue) => {
    setPrice(fieldValue);
  };
  const handleUpdateMarkup = (fieldName = false) => {
    const value = {
      productId: productItemId,
      positions: selectedItems,
    };
    if (fieldName === constant.PRICE) {
      value.price = updatePrice || parseInt(0);
    }
    if (fieldName === constant.INVENTORY_QUANTITY) {
      value.inventoryQuantity = updateQuantity || parseInt(0);
    }
    updateMarkupVariant({ variables: { input: value } })
      .then((res) => {
        setSelectedItems([]);
        const responseError = baseHelper.getResponseError(res, gql.BULK_UPDATE_VARIANT);
        const successMessage =
          fieldName === constant.INVENTORY_QUANTITY
            ? cms("section.image.message.success.updateQuantity")
            : cms("section.variant.message.updatedPrice");
        if (responseError) {
          return handleBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        }
        refetch();
        setOpenQuantity(false);
        setActive(false);
        return handleBanner({
          isOpen: true,
          status: constant.SUCCESS,
          title: successMessage,
        });
      })
      .catch((exception) => {
        setOpenQuantity(false);
        setActive(false);
        handleBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
      });
  };
  const handleDeleteVariant = () => {
    const value = {
      id: selectedId + 1,
      productId: productItemId,
    };
    deleteVariant({ variables: { input: value } })
      .then((res) => {
        const responseError = baseHelper.getResponseError(res.data, gql.DELETE_VARIANT);
        if (responseError) {
          handleDeleteModal();
          handleBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        }
        const responseData = baseHelper.getResponseData(res.data, gql.DELETE_VARIANT);
        if (responseData) {
          setSelectedId(null);
          handleDeleteModal();
          refetch();
          handleBanner({
            isOpen: true,
            status: constant.SUCCESS,
            title: cms("section.image.message.success.variantDelete"),
          });
        }
      })
      .catch((exception) => {
        handleBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
        handleDeleteModal();
      });
  };
  const renderDeleteModal = () => {
    return (
      <Modal
        open={deleteActive}
        onClose={handleDeleteModal}
        title={cms("section.modal.title.delete")}
        primaryAction={{
          content: cms("section.modal.button.primary.delete"),
          onAction: handleDeleteVariant,
          loading: loadingDeleteVariant,
          disabled: loadingDeleteVariant,
        }}
      >
        <Modal.Section>{cms("section.modal.content")}</Modal.Section>
      </Modal>
    );
  };
  const renderModal = () => {
    return (
      <Modal
        open={active}
        onClose={handleEditMarkup}
        title={cms("section.variant.common.updateVariantPrice")}
        primaryAction={{
          content: cms("section.variant.common.updatePrice"),
          onAction: () => handleUpdateMarkup(constant.PRICE),
          loading: loadingBulkUpdate,
          disabled: loadingBulkUpdate,
        }}
      >
        <Modal.Section>
          <TextField
            label={cms("section.price.label.price")}
            value={(updatePrice && updatePrice.toString()) || ""}
            placeholder={constant.ZERO_PRICE}
            type="number"
            min={0}
            step={0.01}
            onChange={(value) => handleChange(parseInt(value, 10))}
          />
        </Modal.Section>
      </Modal>
    );
  };
  const renderQuantityModal = () => {
    return (
      <Modal
        open={openQuantity}
        onClose={handleEditQuantity}
        title={cms("section.modal.title.updateQuantity")}
        primaryAction={{
          content: cms("section.modal.label.updateQuantity"),
          onAction: () => handleUpdateMarkup(constant.INVENTORY_QUANTITY),
          loading: loadingBulkUpdate,
          disabled: loadingBulkUpdate,
        }}
      >
        <Modal.Section>
          <TextField
            label={cms("section.modal.label.quantity")}
            value={(updateQuantity && updateQuantity.toString()) || ""}
            placeholder={constant.ZERO_PRICE}
            type="number"
            min={0}
            onChange={(value) => setQuantity(parseInt(value, 10))}
          />
        </Modal.Section>
      </Modal>
    );
  };
  const renderItem = (item, _, index) => {
    const {
      imageUrl,
      option1,
      option2,
      option3,
      option1Val,
      option2Val,
      option3Val,
      inventoryQuantity,
      price,
      sku = "",
    } = item;

    const media = (
      <Thumbnail
        source={imageHelper.resize({ url: imageUrl, type: constant.imageTypes.THUMBNAIL }) || constant.NOIMAGESNAP}
        alt={`variant image ${index}`}
      />
    );

    return (
      <ResourceItem id={index + 1} media={media}>
        <TextContainer>
          <Stack>
            <Stack.Item fill>
              <div className="variantEllipsis">{option1Val && `${option1} : ${option1Val}`}</div>
              <div className="variantEllipsis">{option2Val && `${option2} : ${option2Val}`}</div>
              <div className="variantEllipsis">{option3Val && `${option3} : ${option3Val}`}</div>
            </Stack.Item>

            <Stack.Item>
              <Popover
                active={activePopover[index]}
                setActive={() => setActivePopover({ [index]: !activePopover[index] })}
                options={[
                  {
                    content: cms("label.edit"),
                    icon: EditMinor,
                    onAction: () => history.push(`/variants/edit/${productItemId}/${index + 1}`),
                  },
                  {
                    content: cms("label.delete"),
                    icon: DeleteMinor,
                    destructive: true,
                    onAction: () => handleDeleteModal(index),
                  },
                ]}
              />
            </Stack.Item>
          </Stack>
          <FormLayout>
            <FormLayout.Group condensed>
              <TextField
                label={cms("section.modal.label.inventory")}
                value={(inventoryQuantity && inventoryQuantity.toString()) || ""}
                disabled
              />
              <TextField
                prefix={moneyFormat}
                label={cms("section.variant.label.price")}
                value={(price && price.toString()) || ""}
                disabled
              />
              <TextField label={cms("section.variant.label.sku")} value={sku} disabled />
            </FormLayout.Group>
          </FormLayout>
        </TextContainer>
      </ResourceItem>
    );
  };
  const view = (dataNew) => {
    const { _id: id, variants } = dataNew;
    return (
      <Layout.Section>
        <Card
          sectioned
          title={cms("section.variant.title")}
          actions={[
            {
              content: cms("section.variant.headerAction"),
              onAction: () => {
                history.push(`/variants/add/${id}`);
              },
            },
          ]}
        >
          {!(variants && variants.length) && (
            <>
              <TextContainer>
                <DisplayText size="extraSmall">{cms("section.variant.content")}</DisplayText>
                <br />
                <DisplayText size="extraSmall">
                  <TextStyle variation="strong">{`${cms("section.variant.note.label")}: `}</TextStyle>
                  {cms("section.variant.note.content")}
                </DisplayText>
              </TextContainer>
            </>
          )}
          {variants && variants.length > 0 && (
            <ResourceList
              items={variants}
              renderItem={renderItem}
              selectedItems={selectedItems}
              onSelectionChange={setSelectedItems}
              bulkActions={bulkActions}
              idForItem={(item, index) => index + 1}
            />
          )}
        </Card>
      </Layout.Section>
    );
  };
  return [renderModal(), renderQuantityModal(), renderDeleteModal(), view(data)];
};
RenderVariants.propTypes = variantsProp.type;
export default RenderVariants;
